import { databases, storage, account, functions } from '@/lib/appwrite';
import type { RequestVisitDocument } from '@/lib/types/requestVisitType';
import { ID, Query } from 'appwrite';

const VISITS_COLLECTION_ID = import.meta.env.VITE_VISITS_COLLECTION_ID;
const VISIT_LOGS_COLLECTION_ID = import.meta.env.VITE_VISIT_LOGS_COLLECTION_ID;
const ORGANIZATIONS_COLLECTION_ID = import.meta.env.VITE_ORGANIZATIONS_COLLECTION_ID;
export const BUCKET_ID = import.meta.env.VITE_BUCKET_ID_SIGNATURES_REQUEST_VISIT;
const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;

export class RequestVisitService {

    //  REFACTORIZADO 
    static async getRequestVisitsByUser(
        userId: string,
        month?: string,
        limit: number = 20,
        offset: number = 0,
        statusFilter?: string
    ): Promise<{ documents: any[], total: number }> {
        try {
            const queries = [];

            // 👇 Ahora busca docs donde el usuario es creador O está incluido como acompañante
            queries.push(
                Query.or([
                    Query.equal('userId', userId),
                    Query.contains('usersId', userId),  // array field
                ])
            );

            if (statusFilter && statusFilter !== 'all') {
                queries.push(Query.equal('status', statusFilter));
            }

            if (month) {
                const [year, monthNum] = month.split('-');
                const startDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                const endDate = new Date(parseInt(year), parseInt(monthNum), 0, 23, 59, 59);

                queries.push(Query.greaterThanEqual('$createdAt', startDate.toISOString()));
                queries.push(Query.lessThanEqual('$createdAt', endDate.toISOString()));
            }

            queries.push(Query.orderDesc('$createdAt'));
            queries.push(Query.limit(limit));
            queries.push(Query.offset(offset));

            const visitsResponse = await databases.listDocuments(
                DATABASE_ID,
                VISITS_COLLECTION_ID,
                queries
            );


            // 2. Procesar cada documento para enriquecer con datos relacionados
            const enrichedDocuments = await Promise.all(
                visitsResponse.documents.map(async (visit) => {
                    let companyName = 'Sin empresa';
                    let contactPerson = null;
                    let contactRole = null;
                    let agreements: string[] = [];
                    let observations: string[] = [];
                    let signature: string[] = [];

                    // 2.1 Obtener nombre de la empresa
                    if (visit.organizationId) {
                        try {
                            const organization = await databases.getDocument(
                                DATABASE_ID,
                                ORGANIZATIONS_COLLECTION_ID,
                                visit.organizationId
                            );
                            companyName = organization.name || 'Sin nombre';
                        } catch (error) {
                            console.warn(`Organización no encontrada: ${visit.organizationId}`);
                        }
                    }

                    // 2.2 Obtener logs de visita (puede no existir)
                    try {
                        const logsResponse = await databases.listDocuments(
                            DATABASE_ID,
                            VISIT_LOGS_COLLECTION_ID,
                            [Query.equal('visitId', visit.$id)]
                        );

                        // Si existe log, extraer datos
                        if (logsResponse.documents.length > 0) {
                            const log = logsResponse.documents[0];
                            contactPerson = log.contactPerson || null;
                            contactRole = log.contactRole || null;
                            agreements = log.agreements || [];
                            observations = log.observations || [];
                            signature = log.signature || [];
                        }
                    } catch (error) {
                        console.warn(`Log no encontrado para visitId: ${visit.$id}`);
                    }

                    // 2.3 Construir objeto combinado (compatible con UI)
                    return {
                        $id: visit.$id,
                        userId: visit.userId,
                        organizationId: visit.organizationId,
                        status: visit.status,
                        visitPurpose: visit.visitPurpose || visit.userComment || '',
                        companyName, // De ORGANIZATIONS
                        contactPerson, // De VISIT_LOGS
                        contactRole, // De VISIT_LOGS
                        agreements, // De VISIT_LOGS
                        observations, // De VISIT_LOGS
                        signature, // De VISIT_LOGS
                        visits: visit.visitDates || [], // visits[] → visitDates[]
                        branch: visit.branch || null,
                        usersId: visit.usersId || [],   

                        $createdAt: visit.$createdAt,
                        $updatedAt: visit.$updatedAt
                    };
                })
            );

            return {
                documents: enrichedDocuments,
                total: visitsResponse.total
            };

        } catch (error) {
            console.error('Error getting request visits:', error);
            throw new Error('Error al obtener las solicitudes');
        }
    }

    // ============ MÉTODOS HELPER EXISTENTES ============
    static getSignatureUrlForReport(fileId: string): string {
        const BUCKET_ID = import.meta.env.VITE_BUCKET_ID_SIGNATURES_REQUEST_VISIT;
        return `${import.meta.env.VITE_APPWRITE_ENDPOINT}/storage/buckets/${BUCKET_ID}/files/${fileId}/view?project=${import.meta.env.VITE_APPWRITE_PROJECT_ID}`;
    }

    static async uploadSignature(blob: Blob): Promise<string> {
        try {
            const file = new File([blob], `signature-${Date.now()}.png`, {
                type: 'image/png'
            });

            const response = await storage.createFile(
                BUCKET_ID,
                ID.unique(),
                file
            );

            return response.$id;
        } catch (error) {
            console.error('Error uploading signature:', error);
            throw new Error('Error al subir la firma');
        }
    }

    static async createRequestVisit(data: RequestVisitDocument): Promise<string> {
        try {
            const response = await databases.createDocument(
                DATABASE_ID,
                VISITS_COLLECTION_ID, // Ahora usa la nueva tabla
                ID.unique(),
                data
            );

            return response.$id;
        } catch (error) {
            console.error('Error creating request visit:', error);
            throw new Error('Error al crear la solicitud de visita');
        }
    }

    static async getCurrentUserId(): Promise<string> {
        try {
            const user = await account.get();
            return user.$id;
        } catch (error) {
            console.error('Error getting user:', error);
            throw new Error('Error al obtener el usuario');
        }
    }

    static async getCurrentLocation(): Promise<{ lat: number; lng: number }> {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocalización no soportada'));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => {
                    console.error('Geolocation error:', error);
                }
            );
        });
    }


    static async createVisitLog(
        visitId: string,
        logData: {
            contactPerson: string;
            contactRole: string;
            agreements: string[];
            observations: string[];
            signature?: string[];
        }
    ): Promise<void> {
        if (!navigator.onLine) {
            throw new Error('OFFLINE_MODE');
        }

        try {
            const existingLogs = await databases.listDocuments(
                DATABASE_ID,
                VISIT_LOGS_COLLECTION_ID,
                [Query.equal('visitId', visitId)]
            );

            // 🆕 VARIABLE PARA SABER SI HAY FIRMA (nueva o existente)
            let hasFinalSignature = false;

            if (existingLogs.documents.length > 0) {
                const existingLog = existingLogs.documents[0];

                // 🆕 VERIFICAR SI YA TENÍA FIRMA ANTES
                const hadSignatureBefore = existingLog.signature && existingLog.signature.length > 0;

                // ACTUALIZAR DOCUMENTO
                await databases.updateDocument(
                    DATABASE_ID,
                    VISIT_LOGS_COLLECTION_ID,
                    existingLog.$id,
                    logData
                );

                // DETERMINAR SI HAY FIRMA AL FINAL
                if (logData.signature) {
                    // Si viene firma nueva, usar esa
                    hasFinalSignature = logData.signature.length > 0;
                } else {
                    // Si NO viene firma nueva, mantener la que había
                    hasFinalSignature = hadSignatureBefore;
                }
            } else {
                // CREAR NUEVO DOCUMENTO
                await databases.createDocument(
                    DATABASE_ID,
                    VISIT_LOGS_COLLECTION_ID,
                    ID.unique(),
                    {
                        visitId,
                        ...logData
                    }
                );

                //  PARA CREACIÓN, SOLO HAY FIRMA SI VIENE EN logData
                hasFinalSignature = logData.signature ? logData.signature.length > 0 : false;
            }

            //  CALCULAR isComplete CON LA FIRMA FINAL
            const isComplete =
                logData.contactPerson?.trim() !== '' &&
                logData.contactRole?.trim() !== '' &&
                hasFinalSignature; //  Usar la variable que considera firma existente

            const newStatus = isComplete ? 'completed' : 'pending';

            await databases.updateDocument(
                DATABASE_ID,
                VISITS_COLLECTION_ID,
                visitId,
                { status: newStatus }
            );

        } catch (error) {
            console.error('Error creating visit log:', error);
            throw error;
        }
    }

    static async getRequestVisitById(documentId: string): Promise<any> {
        try {
            const visit = await databases.getDocument(
                DATABASE_ID,
                VISITS_COLLECTION_ID,
                documentId
            );

            // Enriquecer con datos relacionados
            let companyName = 'Sin empresa';
            let contactPerson = null;
            let contactRole = null;
            let agreements: string[] = [];
            let observations: string[] = [];
            let signature: string[] = [];

            // Obtener organización
            if (visit.organizationId) {
                try {
                    const organization = await databases.getDocument(
                        DATABASE_ID,
                        ORGANIZATIONS_COLLECTION_ID,
                        visit.organizationId
                    );
                    companyName = organization.name || 'Sin nombre';
                } catch (error) {
                    console.warn(`Organización no encontrada: ${visit.organizationId}`);
                }
            }

            // Obtener logs
            try {
                const logsResponse = await databases.listDocuments(
                    DATABASE_ID,
                    VISIT_LOGS_COLLECTION_ID,
                    [Query.equal('visitId', visit.$id)]
                );

                if (logsResponse.documents.length > 0) {
                    const log = logsResponse.documents[0];
                    contactPerson = log.contactPerson || null;
                    contactRole = log.contactRole || null;
                    agreements = log.agreements || [];
                    observations = log.observations || [];
                    signature = log.signature || [];
                }
            } catch (error) {
                console.warn(`Log no encontrado para visitId: ${visit.$id}`);
            }

            return {
                $id: visit.$id,
                userId: visit.userId,
                organizationId: visit.organizationId,
                status: visit.status,
                visitPurpose: visit.visitPurpose || visit.userComment || '',
                companyName,
                contactPerson,
                contactRole,
                agreements,
                observations,
                signature,
                visits: visit.visitDates || [],
                branch: visit.branch || null,

                $createdAt: visit.$createdAt,
                $updatedAt: visit.$updatedAt
            };
        } catch (error) {
            console.error('Error getting request visit:', error);
            throw new Error('Error al obtener la solicitud');
        }
    }

    static async updateRequestVisit(
        documentId: string,
        data: Partial<RequestVisitDocument>
    ): Promise<void> {
        try {
            await databases.updateDocument(
                DATABASE_ID,
                VISITS_COLLECTION_ID,
                documentId,
                data
            );
        } catch (error) {
            console.error('Error updating request visit:', error);
            throw new Error('Error al actualizar la solicitud');
        }
    }

    static async deleteRequestVisit(documentId: string): Promise<void> {
        try {
            await databases.deleteDocument(
                DATABASE_ID,
                VISITS_COLLECTION_ID,
                documentId
            );
        } catch (error) {
            console.error('Error deleting request visit:', error);
            throw new Error('Error al eliminar la solicitud');
        }
    }

    static getSignatureUrl(fileId: string): string {
        return `${import.meta.env.VITE_APPWRITE_ENDPOINT}/storage/buckets/${BUCKET_ID}/files/${fileId}/view?project=${import.meta.env.VITE_APPWRITE_PROJECT_ID}`;
    }

    //  GENERADOR DE REPORTES 
    static async generateMonthlyReport(
        userId: string,
        year: string,
        month: string
    ): Promise<{ success: boolean; fileId?: string; error?: string }> {
        try {
            const FUNCTION_ID = import.meta.env.VITE_DOCS_GENERATOR;

            if (!FUNCTION_ID) {
                throw new Error('Falta VITE_DOCS_GENERATOR');
            }

            const user = await account.get();
            const userName = user.name;

            // Obtener documentos del mes
            const monthFilter = `${year}-${month}`;
            // const result = await this.getRequestVisitsByUser(userId, monthFilter, 100, 0); //sin filtro de estado
            const result = await this.getRequestVisitsByUser(userId, monthFilter, 100, 0, 'completed');
            if (result.documents.length === 0) {
                throw new Error('No hay registros completados en este mes');
            }


            // Preparar datos para el template
            const visits = result.documents.map(doc => {
                const visitDates = doc.visits || [];
                const startDate = visitDates[1] ? new Date(visitDates[1]) : null;
                const endDate = visitDates[2] ? new Date(visitDates[2]) : null;

                const dayName = startDate ? startDate.toLocaleDateString('es-ES', {
                    weekday: 'long'
                }) : '';

                const signature = doc.signature || [];
                const signatureFileId = signature[0] || '';

                return {
                    day: dayName.charAt(0).toUpperCase() + dayName.slice(1),
                    date: startDate ? startDate.toLocaleDateString('es-ES') : '',
                    startTime: startDate ? startDate.toLocaleTimeString('es-ES', {
                        hour: '2-digit', minute: '2-digit'
                    }) : '',
                    endTime: endDate ? endDate.toLocaleTimeString('es-ES', {
                        hour: '2-digit', minute: '2-digit'
                    }) : '',
                    companyName: doc.companyName || '',
                    contactPerson: doc.contactPerson || '',
                    contactRole: doc.contactRole || '',
                    visitPurpose: doc.visitPurpose || '',
                    agreements: (doc.agreements || []).join(', '),
                    observations: (doc.observations || []).join(', '),
                    signatureUrl: signatureFileId ? this.getSignatureUrlForReport(signatureFileId) : ''
                };
            });

            // Preparar payload
            const payload = {
                template_type: 'request_visit_monthly_report',
                data: {
                    MONTH: month,
                    YEAR: year,
                    VISITS: visits,
                    USER_NAME: userName
                }
            };

            // Ejecutar función
            const exec = await functions.createExecution(
                FUNCTION_ID,
                JSON.stringify(payload),
                false
            );

            const response = typeof exec.responseBody === 'string'
                ? JSON.parse(exec.responseBody)
                : exec.responseBody;

            if (!response.success) {
                throw new Error(response.error || 'Error generando documento');
            }

            // Subir a storage
            const documentBase64 = response.document;
            const documentBytes = Uint8Array.from(atob(documentBase64), c => c.charCodeAt(0));
            const blob = new Blob([documentBytes], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });

            const BUCKET_ID = import.meta.env.VITE_BUCKET_ID_DOCS_REQUEST_VISIT;
            const filename = `reporte_visitas_${year}_${month}_${Date.now()}.docx`;
            const file = new File([blob], filename, {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });

            const uploadedFile = await storage.createFile(
                BUCKET_ID,
                ID.unique(),
                file
            );

            return {
                success: true,
                fileId: uploadedFile.$id
            };

        } catch (error: any) {
            console.error('Error generando reporte:', error);
            return {
                success: false,
                error: error.message || 'Error desconocido'
            };
        }
    }

    static getReportUrl(fileId: string): string {
        const BUCKET_ID = import.meta.env.VITE_BUCKET_ID_DOCS_REQUEST_VISIT;
        return `${import.meta.env.VITE_APPWRITE_ENDPOINT}/storage/buckets/${BUCKET_ID}/files/${fileId}/view?project=${import.meta.env.VITE_APPWRITE_PROJECT_ID}`;
    }
}